import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaTimes, FaTag, FaLeaf } from 'react-icons/fa';
import {
  getEnabledVariants,
  getMenuItemUnavailableLabel,
  getVariantUnavailableLabel,
  isMenuItemOrderable,
  isVariableMenuItem,
  isVariantOrderable,
} from '../../pages/OurMenu/ourMenuUtils';
import MenuItemActions from '../../pages/OurMenu/MenuItemActions';
import './MenuItemModal.css';

const parentFallbackDescription = (item) =>
  `A delicious, freshly prepared ${String(item.category || 'menu')
    .toLowerCase()} dish made with hand-picked ingredients and traditional recipes. No preservatives, no artificial flavours — just pure home-style goodness delivered to your door.`;

const MenuItemModal = ({ item, onClose, openIntent = null }) => {
  const variable = isVariableMenuItem(item);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [needsSizePrompt, setNeedsSizePrompt] = useState(false);
  const [sizePulse, setSizePulse] = useState(false);
  const variantsSectionRef = useRef(null);
  const primaryAction = openIntent === 'plan' ? 'plan' : 'cart';

  useEffect(() => {
    setSelectedVariantId(null);
    setNeedsSizePrompt(false);
    setSizePulse(false);
  }, [item?.id]);

  useEffect(() => {
    if (selectedVariantId != null) {
      setNeedsSizePrompt(false);
      setSizePulse(false);
    }
  }, [selectedVariantId]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const selectedVariant = useMemo(() => {
    if (!variable || selectedVariantId == null) return null;
    return (item.variants || []).find(
      (v) => String(v.itemID) === String(selectedVariantId),
    );
  }, [variable, item?.variants, selectedVariantId]);

  const orderable = variable
    ? getEnabledVariants(item).length > 0
    : isMenuItemOrderable(item);
  const unavailableLabel = getMenuItemUnavailableLabel(item);

  const promptSelectSize = () => {
    setNeedsSizePrompt(true);
    setSizePulse(true);
    variantsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => setSizePulse(false), 900);
  };

  // Parent media by default; swap to child image / description when an option is chosen.
  const displayImage = selectedVariant?.image || item.image;
  const displayFallback =
    selectedVariant?.fallbackImage || item.fallbackImage || item.image;
  const displayDescription =
    (selectedVariant?.description && selectedVariant.description.trim()) ||
    (item.description && item.description.trim()) ||
    parentFallbackDescription(item);
  const displaySubtitle =
    selectedVariant?.name ||
    (selectedVariant?.label ? `${item.name} · ${selectedVariant.label}` : null);

  const priceDisplay = selectedVariant?.priceDisplay
    ? selectedVariant.priceDisplay
    : variable
      ? item.fromPriceDisplay || item.priceDisplay || 'Select an option'
      : item.priceDisplay;

  if (!item) return null;

  return (
    <div className="mim-overlay" onClick={onClose}>
      <div className="mim-modal" onClick={(e) => e.stopPropagation()}>
        <button className="mim-close" onClick={onClose} aria-label="Close">
          <FaTimes />
        </button>

        <div className="mim-img-wrap">
          <img
            key={displayImage}
            src={displayImage}
            alt={displaySubtitle || item.name}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = displayFallback;
            }}
          />
          <span className="mim-category">{item.category}</span>
        </div>

        <div className="mim-body">
          <h2 className="mim-name">{item.name}</h2>
          {displaySubtitle && selectedVariant ? (
            <p className="mim-variant-name">{displaySubtitle}</p>
          ) : null}

          <div className="mim-meta">
            <span className="mim-meta__item">
              <FaTag className="mim-meta__icon" />
              {item.category}
            </span>
            <span className="mim-meta__item">
              <FaLeaf className="mim-meta__icon mim-meta__icon--green" />
              Fresh Daily
            </span>
          </div>

          <p className="mim-desc">{displayDescription}</p>

          {variable ? (
            <div
              ref={variantsSectionRef}
              className={`mim-variants${needsSizePrompt ? ' mim-variants--needs-selection' : ''}${
                sizePulse ? ' mim-variants--pulse' : ''
              }`}
            >
              <p className="mim-variants__label">Choose an option</p>
              <div
                className="mim-variants__list"
                role="radiogroup"
                aria-label={`Options for ${item.name}`}
                aria-invalid={needsSizePrompt || undefined}
              >
                {(item.variants || []).map((v) => {
                  const enabled = isVariantOrderable(v);
                  const selected = String(selectedVariantId) === String(v.itemID);
                  return (
                    <button
                      key={v.itemID}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={!enabled}
                      className={`mim-variant${selected ? ' mim-variant--selected' : ''}${
                        !enabled ? ' mim-variant--disabled' : ''
                      }`}
                      onClick={() => {
                        if (!enabled) return;
                        setSelectedVariantId(v.itemID);
                      }}
                    >
                      <span className="mim-variant__label">{v.label}</span>
                      <span className="mim-variant__price">
                        {enabled
                          ? v.priceDisplay
                          : getVariantUnavailableLabel(v)}
                      </span>
                    </button>
                  );
                })}
              </div>
              {needsSizePrompt || !selectedVariant ? (
                <p
                  className={`mim-variants__hint${needsSizePrompt ? ' mim-variants__hint--error' : ''}`}
                  role="status"
                  aria-live="polite"
                >
                  {needsSizePrompt ? 'Please choose an option' : 'Select an option to add this item.'}
                </p>
              ) : null}
            </div>
          ) : (
            <ul className="mim-highlights">
              <li>✔ No preservatives</li>
              <li>✔ Home-style preparation</li>
              <li>✔ Delivered fresh &amp; hot</li>
            </ul>
          )}

          <div className="mim-footer">
            <span className="mim-price">{priceDisplay}</span>
            <MenuItemActions
              item={item}
              orderable={orderable}
              unavailableLabel={unavailableLabel}
              variant="modal"
              className="mim-btn"
              addedClassName="mim-btn--added"
              planClassName="mim-btn mim-btn--plan"
              selectedVariant={selectedVariant}
              primaryAction={primaryAction}
              onAfterAdd={onClose}
              onOpenOptions={variable && !selectedVariant ? promptSelectSize : undefined}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuItemModal;
